import React from 'react';
import { Progress, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from 'reactstrap';
import KioskWorkoutsTab from "./KioskWorkoutsTab";
import EvaluationNavigator from "./EvaluationNavigator";
import new1Bg from "../../../assets/images/new1Bg.png";
import neoLogo from "../../../assets/images/Neo_Logo_A.svg";
import GamificationBadges from "./GamificationBadges";
import RankingLeaderboard from "./RankingLeaderboard";
import KioskCertificate from "./KioskCertificate";
import moment from "moment";
import { useKioskGamification } from "../hooks/useKioskGamification";


const KioskGamificationProfile = ({ member, onBack, printOnlyMode = false, tenantLogo = null }) => {
    const {
        liveMember,
        allEvaluations,
        selectedEvalIndex,
        latestEvaluation,
        groupedTopics,
        gamifiedActivities,
        achievementsSettings,
        rankSettings,
        awardedAchievementIds,
        awardedAchievements,
        loadingEval,
        activeTab,
        levels,
        rankingData,
        loadingRanking,
        actions: { setActiveTab, navigateEvaluation }
    } = useKioskGamification(member);

    const [showNextChallenges, setShowNextChallenges] = React.useState(false);
    const [showCertificate, setShowCertificate] = React.useState(false);


    const currentActivityIndex = latestEvaluation ? gamifiedActivities.findIndex(a => a.id === latestEvaluation.idActivity) : -1;
    const nextActivity = currentActivityIndex >= 0 && currentActivityIndex < gamifiedActivities.length - 1 ? gamifiedActivities[currentActivityIndex + 1] : null;
    
    const nextGroupedTopics = React.useMemo(() => {
        if (!nextActivity || !nextActivity.objectives) return {};
        const grouped = {};
        nextActivity.objectives.forEach(obj => {
            grouped[obj.name || "Geral"] = obj.topics || [];
        });
        return grouped;
    }, [nextActivity]);

    // Se for modo apenas impressão, força a aba treinos
    React.useEffect(() => {
        if (printOnlyMode) {
            setActiveTab('treinos');
        }
    }, [printOnlyMode, setActiveTab]);
    const photo = liveMember?.photoUrl || liveMember?.photo || null;
    const name = liveMember?.fullName || "Aluno(a)";
    const points = liveMember?.xpTotal || liveMember?.XP || 0;
    const level = (liveMember?.activityNames && liveMember?.activityNames.length > 0) ? liveMember?.activityNames[0] : "Iniciante";

    const filteredLeaderboard = React.useMemo(() => {
        if (!rankingData?.leaderboard || !liveMember) return [];
        
        const studentActivities = liveMember.activityNames || [];
        if (studentActivities.length === 0) return rankingData.leaderboard;
        
        const filtered = rankingData.leaderboard.filter(entry => {
            if (!entry.activityNames || entry.activityNames.length === 0) return true; // Mantém fallback para legados
            return entry.activityNames.some(act => studentActivities.includes(act));
        });
        
        // Recalcular posições relativas na atividade
        return filtered.map((entry, index) => ({
            ...entry,
            position: index + 1
        }));
    }, [rankingData?.leaderboard, liveMember]);

    const memberPosition = React.useMemo(() => {
        if (filteredLeaderboard.length === 0) return null;
        const entry = filteredLeaderboard.find(e => e.id === member?.id);
        return entry?.position || null;
    }, [filteredLeaderboard, member?.id]);

    return (
        <div
            className="w-100 flex-grow-1 d-flex flex-column position-relative"
            style={{
                backgroundColor: "#001D3D", // Fallback color for underwater theme
                backgroundImage: `url(${new1Bg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                overflowY: 'auto',
                paddingBottom: '100px'
            }}
        >

            {/* Overlay claro e desfocado sobre o fundo */}
            <div style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(240, 242, 246, 0.94)',
                zIndex: 0
            }} />

            {/* Header / Gamification Bar (Faixa Horizontal no Topo) */}
            <div
                className="w-100 position-relative shadow-lg flex-shrink-0"
                style={{
                    backgroundColor: 'rgba(0, 15, 30, 0.85)',
                    minHeight: '140px',
                    display: 'flex',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
            >
                {/* Overlay Escuro para Legibilidade */}
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(10,25,50,0.4) 50%, rgba(0,0,0,0.6) 100%)',
                    zIndex: 1
                }} />

                {/* HEADER DESKTOP (Idêntico ao original) */}
                <div className="container-fluid px-3 py-2 position-relative d-none d-md-flex flex-row align-items-center justify-content-between gap-2" style={{ zIndex: 2 }}>
                    {/* Avatar (Lado Esquerdo) */}
                    <div className="d-flex flex-row align-items-center gap-2" style={{ maxWidth: "65%" }}>
                        <div className="d-flex align-items-center gap-2">
                            {/* Avatar Circular com Borda XP */}
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center shadow-lg bg-dark flex-shrink-0 overflow-hidden"
                                style={{
                                    width: "80px",
                                    height: "80px",
                                    border: "3px solid #f1b44c", // Borda dourada XP mais visível
                                    backgroundColor: "rgba(255,255,255,0.1)"
                                }}
                            >
                                {photo ? (
                                    <img src={photo} alt={name} className="img-fluid w-100 h-100" style={{ objectFit: 'cover' }} />
                                ) : (
                                    <span className="text-white-50 d-flex align-items-center justify-content-center w-100 h-100">
                                        <i className="mdi mdi-account font-size-40"></i>
                                    </span>
                                )}
                            </div>

                            {/* Informações de Nome e Rank */}
                            <div>
                                <h4 className="fw-bold text-white mb-0 shadow-sm"
                                    style={{
                                        letterSpacing: "-0.5px",
                                        fontSize: name.length > 25 ? "1.4rem" : name.length > 15 ? "1.6rem" : "2rem",
                                        lineHeight: "1.2",
                                        wordBreak: "break-word"
                                    }}>
                                    {name}
                                </h4>
                                <div className="d-flex align-items-center gap-1 mt-1">
                                    <span className="badge px-3 py-1 font-size-14 fw-bold text-uppercase rounded-pill" style={{ backgroundColor: '#f1b44c', color: '#000', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                        <i className="mdi mdi-star me-1"></i> {level}
                                    </span>
                                    {memberPosition ? (
                                        <span className="badge px-3 py-1 font-size-14 fw-bold text-uppercase rounded-pill bg-dark border" style={{ color: '#f1b44c', borderColor: '#f1b44c' }}>
                                            #{memberPosition} no Ranking
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Logo e Informações de Gamificação (Lado Direito) */}
                    <div className="d-flex flex-row align-items-center justify-content-end flex-shrink-0">
                        {/* Indicador de XP */}
                        <div className="d-flex flex-row align-items-center bg-dark rounded-pill px-2 py-1 shadow-sm border" style={{ borderColor: 'rgba(241, 180, 76, 0.3)', backgroundColor: 'rgba(0,0,0,0.5) !important' }}>
                            <div className="avatar-sm d-flex align-items-center justify-content-center bg-warning bg-opacity-25 rounded-circle me-1" style={{ width: '40px', height: '40px' }}>
                                <i className="mdi mdi-trophy text-warning font-size-22"></i>
                            </div>
                            <div className="text-end pe-2">
                                <h6 className="text-white-50 text-uppercase fw-bold mb-0" style={{ fontSize: "12px", letterSpacing: "1px" }}>Pontos</h6>
                                <h4 className="text-warning fw-bold mb-0 d-flex align-items-baseline gap-1" style={{ fontSize: "1.6rem", textShadow: "0 2px 5px rgba(0,0,0,0.5)" }}>
                                    {points} <span className="font-size-14 text-white-50 fw-normal">XP</span>
                                </h4>
                            </div>
                        </div>
                    </div>
                </div>

                {/* HEADER MOBILE (Adaptado) */}
                <div className="container-fluid px-3 py-3 position-relative d-flex d-md-none flex-row align-items-center justify-content-between gap-2" style={{ zIndex: 2 }}>

                    {/* Avatar + Nome (Lado Esquerdo) */}
                    <div className="d-flex flex-row align-items-center gap-2" style={{ maxWidth: "65%" }}>
                        {/* Avatar Circular com Borda XP */}
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center shadow-lg bg-dark flex-shrink-0 overflow-hidden"
                            style={{
                                width: "70px",
                                height: "70px",
                                border: "3px solid #f1b44c",
                                backgroundColor: "rgba(255,255,255,0.1)"
                            }}
                        >
                            {photo ? (
                                <img src={photo} alt={name} className="img-fluid w-100 h-100" style={{ objectFit: 'cover' }} />
                            ) : (
                                <span className="text-white-50 d-flex align-items-center justify-content-center w-100 h-100">
                                    <i className="mdi mdi-account font-size-40"></i>
                                </span>
                            )}
                        </div>

                        {/* Informações de Nome e Rank */}
                        <div>
                            <h4 className="fw-bold text-white mb-0 shadow-sm"
                                style={{
                                    letterSpacing: "-0.5px",
                                    fontSize: name.length > 25 ? "1.2rem" : name.length > 15 ? "1.4rem" : "1.6rem",
                                    lineHeight: "1.2",
                                    wordBreak: "break-word"
                                }}>
                                {name}
                            </h4>
                            <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                                <span className="badge px-2 py-1 font-size-12 fw-bold text-uppercase rounded-pill" style={{ backgroundColor: '#f1b44c', color: '#000', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                    <i className="mdi mdi-star me-1"></i> {level}
                                </span>
                                {memberPosition ? (
                                    <span className="badge px-2 py-1 font-size-12 fw-bold text-uppercase rounded-pill bg-dark border" style={{ color: '#f1b44c', borderColor: '#f1b44c' }}>
                                        #{memberPosition} Ranking
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Indicador de XP (Lado Direito) */}
                    <div className="d-flex flex-row align-items-center bg-dark rounded-pill px-2 py-1 shadow-sm border flex-shrink-0" style={{ borderColor: 'rgba(241, 180, 76, 0.3)', backgroundColor: 'rgba(0,0,0,0.5) !important' }}>
                        <div className="avatar-sm d-flex align-items-center justify-content-center bg-warning bg-opacity-25 rounded-circle me-1" style={{ width: '36px', height: '36px' }}>
                            <i className="mdi mdi-trophy text-warning font-size-20"></i>
                        </div>
                        <div className="text-end pe-2">
                            <h6 className="text-white-50 text-uppercase fw-bold mb-0" style={{ fontSize: "10px", letterSpacing: "1px" }}>Pontos</h6>
                            <h4 className="text-warning fw-bold mb-0 d-flex align-items-baseline gap-1" style={{ fontSize: "1.4rem", textShadow: "0 2px 5px rgba(0,0,0,0.5)" }}>
                                {points} <span className="font-size-12 text-white-50 fw-normal">XP</span>
                            </h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs de Navegação - Oculto no modo Print Only */}
            {!printOnlyMode && (
                <div className="container mt-4 position-relative px-3" style={{ zIndex: 2 }}>
                    {/* TABS DESKTOP */}
                    <Nav pills className="navtab-bg nav-justified shadow-sm rounded-pill p-1 mx-2 mb-3 d-none d-md-flex" style={{ backgroundColor: "rgba(255, 255, 255, 0.93)", border: "1px solid rgba(255,255,255,0.7)" }}>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'avaliacao' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'avaliacao' ? '#fff' : '#111', boxShadow: activeTab === 'avaliacao' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none' }}
                                className="fw-bold rounded-pill py-3 font-size-18"
                                onClick={() => setActiveTab('avaliacao')}
                            >
                                Avaliação
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'conquistas' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'conquistas' ? '#fff' : '#111', boxShadow: activeTab === 'conquistas' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none' }}
                                className="fw-bold rounded-pill py-3 font-size-18"
                                onClick={() => setActiveTab('conquistas')}
                            >
                                Conquistas
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'ranking' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'ranking' ? '#fff' : '#111', boxShadow: activeTab === 'ranking' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none' }}
                                className="fw-bold rounded-pill py-3 font-size-18"
                                onClick={() => setActiveTab('ranking')}
                            >
                                Ranking
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'treinos' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'treinos' ? '#fff' : '#111', boxShadow: activeTab === 'treinos' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none' }}
                                className="fw-bold rounded-pill py-3 font-size-18"
                                onClick={() => setActiveTab('treinos')}
                            >
                                Treinos
                            </NavLink>
                        </NavItem>
                    </Nav>

                    {/* TABS MOBILE */}
                    <Nav pills className="navtab-bg flex-row nav-justified shadow-sm rounded-pill p-1 mb-3 d-flex d-md-none w-100" style={{ backgroundColor: "rgba(255, 255, 255, 0.93)", border: "1px solid rgba(255,255,255,0.7)" }}>
                        <NavItem style={{ flex: '1 1 0' }}>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'avaliacao' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'avaliacao' ? '#fff' : '#111', boxShadow: activeTab === 'avaliacao' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none', fontSize: '13px', padding: '12px 4px' }}
                                className="fw-bold rounded-pill d-flex align-items-center justify-content-center text-center h-100"
                                onClick={() => setActiveTab('avaliacao')}
                            >
                                Avaliação
                            </NavLink>
                        </NavItem>
                        <NavItem style={{ flex: '1 1 0' }}>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'conquistas' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'conquistas' ? '#fff' : '#111', boxShadow: activeTab === 'conquistas' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none', fontSize: '13px', padding: '12px 4px' }}
                                className="fw-bold rounded-pill d-flex align-items-center justify-content-center text-center h-100"
                                onClick={() => setActiveTab('conquistas')}
                            >
                                Conquistas
                            </NavLink>
                        </NavItem>
                        <NavItem style={{ flex: '1 1 0' }}>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'ranking' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'ranking' ? '#fff' : '#111', boxShadow: activeTab === 'ranking' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none', fontSize: '13px', padding: '12px 4px' }}
                                className="fw-bold rounded-pill d-flex align-items-center justify-content-center text-center h-100"
                                onClick={() => setActiveTab('ranking')}
                            >
                                Ranking
                            </NavLink>
                        </NavItem>
                        <NavItem style={{ flex: '1 1 0' }}>
                            <NavLink
                                style={{ cursor: "pointer", transition: "all 0.3s", background: activeTab === 'treinos' ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' : 'transparent', color: activeTab === 'treinos' ? '#fff' : '#111', boxShadow: activeTab === 'treinos' ? '0 4px 10px rgba(0,0,0,0.3)' : 'none', fontSize: '13px', padding: '12px 4px' }}
                                className="fw-bold rounded-pill d-flex align-items-center justify-content-center text-center h-100"
                                onClick={() => setActiveTab('treinos')}
                            >
                                Treinos
                            </NavLink>
                        </NavItem>
                    </Nav>
                </div>
            )}

            <TabContent activeTab={printOnlyMode ? 'treinos' : activeTab} className="container position-relative mt-4" style={{ zIndex: 2, flexGrow: 1 }}>

                {/* ───────────────────────────────────────────────────────── */}
                {/* TAB 1: MEU DESEMPENHO (AVALIAÇÃO TÉCNICA)                 */}
                {/* ───────────────────────────────────────────────────────── */}
                <TabPane tabId="avaliacao">
                    <div style={{
                        padding: '16px',
                        marginBottom: '40px'
                    }}>
                        {loadingEval ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                            </div>
                        ) : latestEvaluation ? (
                            <div className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h3 className="mb-0 fw-bold text-dark">
                                            {showNextChallenges ? `Próximo Estágio: ${nextActivity?.name || ""}` : (latestEvaluation.eventName || "Avaliação Técnica")}
                                        </h3>
                                        <p className="text-muted mb-0 font-size-16">
                                            <i className="mdi mdi-calendar-blank me-1"></i>
                                            {showNextChallenges ? "Confira seus próximos desafios" : moment(latestEvaluation.date).format("DD/MM/YYYY")}
                                        </p>
                                    </div>
                                    <div className="text-end d-flex flex-column align-items-end gap-2">
                                        {!showNextChallenges && (
                                            <span className={`badge rounded-pill px-3 py-2 ${latestEvaluation.status === "PASSED" ? "bg-success text-white" : "bg-warning text-dark"} shadow-sm`}>
                                                {latestEvaluation.status === "PASSED" ? (
                                                    <><i className="mdi mdi-check-circle me-1"></i> Aprovado</>
                                                ) : (
                                                    <><i className="mdi mdi-clock-outline me-1"></i> Em Progresso</>
                                                )}
                                            </span>
                                        )}
                                        {nextActivity && (
                                            <>
                                                <style>
                                                    {`
                                                    @keyframes pulseThreeTimes {
                                                        0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(85, 110, 230, 0.7); }
                                                        50% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(85, 110, 230, 0); }
                                                        100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(85, 110, 230, 0); }
                                                    }
                                                    .btn-pulse-3x {
                                                        animation: pulseThreeTimes 1.2s ease-in-out 3;
                                                    }
                                                    `}
                                                </style>
                                                <button 
                                                    onClick={() => setShowNextChallenges(!showNextChallenges)}
                                                    className={`btn fw-bold rounded-pill shadow px-4 py-2 font-size-16 ${showNextChallenges ? 'btn-outline-secondary' : 'btn-primary btn-pulse-3x'}`}
                                                >
                                                    {showNextChallenges ? "Voltar" : "Ver +"}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {!showNextChallenges && (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2 font-size-18">
                                            <span className="fw-bold text-dark">Progresso para o próximo nível</span>
                                            <span className="fw-bold text-primary">{latestEvaluation.progressPercent || 0}%</span>
                                        </div>
                                        <Progress value={latestEvaluation.progressPercent || 0} color="primary" style={{ height: "14px", backgroundColor: "rgba(0,0,0,0.1)" }} />
                                    </div>
                                )}

                                {/* Banner de Certificado — exibe quando o aluno foi aprovado */}
                                {!showNextChallenges && latestEvaluation.status === "PASSED" && (
                                    <>
                                        <style>
                                            {`
                                            @keyframes cardPulseGlow {
                                                0% { transform: scale(1); box-shadow: 0 8px 24px rgba(234, 179, 8, 0.2), inset 0 0 16px rgba(234, 179, 8, 0.1); }
                                                50% { transform: scale(1.015); box-shadow: 0 12px 36px rgba(234, 179, 8, 0.35), inset 0 0 24px rgba(234, 179, 8, 0.15); }
                                                100% { transform: scale(1); box-shadow: 0 8px 24px rgba(234, 179, 8, 0.2), inset 0 0 16px rgba(234, 179, 8, 0.1); }
                                            }
                                            @keyframes iconBounce {
                                                0%, 100% { transform: translateY(0); }
                                                50% { transform: translateY(-4px); }
                                            }
                                            .legendary-cert-card {
                                                transition: all 0.3s ease;
                                            }
                                            .legendary-cert-card:hover {
                                                transform: translateY(-2px) scale(1.01);
                                                filter: brightness(1.15);
                                            }
                                            `}
                                        </style>
                                        <div
                                            onClick={() => setShowCertificate(true)}
                                            className="legendary-cert-card d-flex align-items-center justify-content-between p-4 mb-4 shadow-lg position-relative overflow-hidden"
                                            style={{
                                                background: 'linear-gradient(135deg, #001529 0%, #002d54 100%)',
                                                borderRadius: '20px',
                                                border: '2px solid #eab308',
                                                animation: 'cardPulseGlow 3.5s infinite ease-in-out',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {/* Light reflection decoration inside card */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '-50%',
                                                    left: '-50%',
                                                    width: '200%',
                                                    height: '200%',
                                                    background: 'radial-gradient(circle, rgba(254,240,138,0.06) 0%, rgba(255,255,255,0) 70%)',
                                                    pointerEvents: 'none'
                                                }}
                                            />

                                            <div className="d-flex align-items-center gap-3 position-relative z-1">
                                                {/* Glowing Icon Badge */}
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle"
                                                    style={{
                                                        width: '56px',
                                                        height: '56px',
                                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.5)',
                                                        animation: 'iconBounce 2s infinite ease-in-out',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <i className="mdi mdi-medal text-white" style={{ fontSize: '32px' }}></i>
                                                </div>
                                                
                                                <div>
                                                    <Badge color="warning" className="mb-2 fw-bold text-dark text-uppercase px-2 py-1 rounded-pill" style={{ letterSpacing: '0.5px', fontSize: '10px' }}>
                                                        Nível Conquistado 🏆
                                                    </Badge>
                                                    <h4 className="mb-1 fw-bold text-white animate-fade-in" style={{ letterSpacing: '-0.3px', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                                                        Certificado de Conquista
                                                    </h4>
                                                    <p className="mb-0 text-light text-opacity-80 small" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                                        Parabéns! Toque aqui para visualizar e salvar seu certificado
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center gap-2 position-relative z-1">
                                                <span className="badge rounded-pill bg-warning text-dark fw-bold px-3 py-2 shadow-sm d-none d-sm-inline-block" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                                    VER CERTIFICADO
                                                </span>
                                                <i className="mdi mdi-chevron-right text-warning fs-2" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}></i>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {Object.keys(showNextChallenges ? nextGroupedTopics : groupedTopics).length > 0 && (
                                    <div className="mt-4">
                                        <h5 className="fw-bold text-secondary mb-4 text-uppercase" style={{ letterSpacing: "1px" }}>
                                            {showNextChallenges ? "Objetivos do Próximo Estágio" : "Tópicos Avaliados"}
                                        </h5>
                                        <div className="table-responsive">
                                            <table className="table table-borderless mb-0 bg-transparent" style={{ backgroundColor: 'transparent' }}>
                                                <tbody className="bg-transparent">
                                                    {Object.entries(showNextChallenges ? nextGroupedTopics : groupedTopics).map(([objectiveName, topicsList], objIdx) => (
                                                        <React.Fragment key={objIdx}>
                                                            <tr className="bg-transparent border-0">
                                                                <td colSpan="2" className="pt-4 pb-2 px-0 bg-transparent">
                                                                    <div className="fw-bold text-dark mb-0" style={{ borderLeft: "5px solid #f1b44c", paddingLeft: "12px", fontSize: "1.4rem" }}>
                                                                        {objectiveName}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {topicsList.map((topic, idx) => {
                                                                const uniqueId = `topic-${objIdx}-${idx}`;
                                                                return (
                                                                    <tr key={uniqueId} className="bg-transparent" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", backgroundColor: 'transparent' }}>
                                                                        <td className="py-2 px-0 bg-transparent align-middle" style={{ backgroundColor: 'transparent', width: '60%', maxWidth: '150px' }}>
                                                                            <div className="d-flex align-items-center flex-wrap gap-1">
                                                                                <div
                                                                                    className="fw-semibold text-dark font-size-16 font-size-md-18 mb-1 mb-md-0"
                                                                                    style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.2' }}
                                                                                >
                                                                                    {topic.name}
                                                                                </div>
                                                                                {topic.isFundamental && (
                                                                                    <span className="badge bg-danger-subtle text-danger font-size-12 mb-1 mb-md-0 d-inline-block" title="Fundamental">Fund.</span>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td className="py-2 text-end px-0 align-middle bg-transparent" style={{ backgroundColor: 'transparent', width: '40%', minWidth: '120px' }}>
                                                                            {levels && levels.length > 0 ? (
                                                                                <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center justify-content-end gap-1">
                                                                                    <span className="me-sm-3 mb-1 mb-sm-0 font-size-12 font-size-sm-16 fw-bold text-muted text-truncate" style={{ maxWidth: "120px" }}>
                                                                                        {topic.levelName || (showNextChallenges ? "A avaliar" : "Avaliado")}
                                                                                    </span>
                                                                                    <div className="d-flex gap-1 gap-sm-2" title={topic.levelName || "Avaliado"}>
                                                                                        {levels.slice(1).map((lvl, lIdx) => {
                                                                                            // Encontra a posição do nível do critério na array total de níveis
                                                                                            const topicLevelIndex = levels.findIndex(l => String(l.id) === String(topic.idLevel));
                                                                                            // Se for o 1º nível (index 0), será todos apagados, pois topicLevelIndex (0) não é > lIdx (que começa em 0).
                                                                                            // Se for o 2º nível (index 1), preenche 1 bloco (lIdx 0).
                                                                                            // Se for o 4º nível "Bom" (index 3), preenche 3 blocks (lIdx 0, 1 e 2).
                                                                                            const isFilled = topicLevelIndex !== -1 && lIdx < topicLevelIndex;
                                                                                            return (
                                                                                                <div
                                                                                                    key={`lvl-block-${lvl.id || lIdx}`}
                                                                                                    style={{
                                                                                                        width: 'clamp(14px, 4vw, 24px)',
                                                                                                        height: 'clamp(14px, 4vw, 24px)',
                                                                                                        backgroundColor: isFilled ? '#f1b44c' : 'rgba(0,0,0,0.1)',
                                                                                                        borderRadius: '4px',
                                                                                                        boxShadow: isFilled ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                                                                                                        transition: 'all 0.3s ease'
                                                                                                    }}
                                                                                                    title={lvl.name || "Nível"}
                                                                                                />
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="fw-bold px-2 px-sm-3 py-1 font-size-12 font-size-sm-14 rounded text-dark border border-secondary border-opacity-25 text-wrap d-inline-block" style={{ backgroundColor: 'transparent', maxWidth: '100%' }}>
                                                                                    {topic.levelName || (showNextChallenges ? "A avaliar" : "Avaliado")}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {latestEvaluation.observations && !showNextChallenges && (
                                    <div className="mt-4 p-3 rounded text-muted border border-secondary border-opacity-25" style={{ backgroundColor: 'transparent' }}>
                                        <strong className="d-block mb-1 text-dark">Observações:</strong>
                                        {latestEvaluation.observations}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex flex-column align-items-center justify-content-center text-muted text-center p-5" style={{ minHeight: "200px" }}>
                                <h5 className="fw-bold text-dark">Nenhuma Avaliação</h5>
                                <p className="mb-0">Não encontramos avaliações recentes registradas para você.</p>
                            </div>
                        )}
                    </div>
                </TabPane>

                {/* ───────────────────────────────────────────────────────── */}
                {/* TAB 2: CONQUISTAS (TRILHA DE INSÍGNIAS)                     */}
                {/* ───────────────────────────────────────────────────────── */}
                <TabPane tabId="conquistas">
                    <div style={{
                        padding: '24px 12px',
                        marginBottom: '40px'
                    }}>
                        <GamificationBadges
                            gamifiedActivities={gamifiedActivities}
                            member={liveMember}
                            achievementsSettings={achievementsSettings}
                            rankSettings={rankSettings}
                            awardedAchievementIds={awardedAchievementIds}
                            awardedAchievements={awardedAchievements}
                        />
                    </div>
                </TabPane>

                {/* ───────────────────────────────────────────────────────── */}
                {/* TAB 3: RANKING (LEADERBOARD DE XP)                       */}
                {/* ───────────────────────────────────────────────────────── */}
                <TabPane tabId="ranking">
                    <div style={{
                        padding: '24px 12px',
                        marginBottom: '40px'
                    }}>
                        <RankingLeaderboard
                            leaderboard={filteredLeaderboard}
                            currentMemberId={liveMember?.id}
                            lastUpdatedAt={rankingData?.lastUpdatedAt}
                            loading={loadingRanking}
                        />
                    </div>
                </TabPane>

                {/* ───────────────────────────────────────────────────────── */}
                {/* TAB 4: TREINOS (IMPRESSÃO TÉRMICA)                         */}
                {/* ───────────────────────────────────────────────────────── */}
                <TabPane tabId="treinos">
                    <KioskWorkoutsTab member={liveMember} tenantLogo={tenantLogo} />
                </TabPane>

            </TabContent>

            {/* Widget fixo de navegação entre avaliações (lateral direita) */}
            {activeTab === 'avaliacao' && !printOnlyMode && (
                <EvaluationNavigator
                    evaluations={allEvaluations}
                    selectedIndex={selectedEvalIndex}
                    onNavigate={navigateEvaluation}
                />
            )}

            {/* Modal do Certificado */}
            {showCertificate && latestEvaluation && (
                <KioskCertificate
                    memberName={name}
                    evaluationDate={latestEvaluation.date}
                    tenantLogo={tenantLogo}
                    activityBadgeUrl={
                        (gamifiedActivities.find(a => a.id === latestEvaluation.idActivity) || {}).photoUrl || null
                    }
                    activityName={
                        (gamifiedActivities.find(a => a.id === latestEvaluation.idActivity) || {}).name || null
                    }
                    memberPhone={
                        liveMember?.cellPhone || 
                        liveMember?.phone || 
                        liveMember?.responsavelPhone || 
                        member?.cellPhone || 
                        member?.phone || 
                        member?.responsavelPhone
                    }
                    onClose={() => setShowCertificate(false)}
                />
            )}

            {/* Botão de Voltar no Canto Inferior Esquerdo */}
            <div className="position-fixed" style={{ bottom: '24px', left: '24px', zIndex: 10 }}>
                <button
                    onClick={onBack}
                    className="btn btn-dark bg-opacity-75 rounded-circle d-flex align-items-center justify-content-center shadow-lg border-0"
                    style={{ width: "64px", height: "64px", transition: "all 0.2s" }}
                    title="Voltar"
                >
                    <i className="mdi mdi-arrow-left text-white" style={{ fontSize: "32px", marginLeft: "-2px" }}></i>
                </button>
            </div>

            {/* Powered By (Canto Inferior Direito) */}
            <div className="position-fixed" style={{ bottom: '24px', right: '24px', zIndex: 10 }}>
                <div className="d-flex align-items-center gap-2 opacity-50">
                    <span style={{ fontSize: "10px", fontWeight: "700", color: "#333", letterSpacing: "1px" }}>POWERED BY</span>
                    <img src={neoLogo} alt="NeoApp" style={{ height: "14px", filter: "grayscale(100%) brightness(0.2)" }} />
                </div>
            </div>
        </div>
    );
};

export default KioskGamificationProfile;
